/**
 * CartRemovalSecuritySettings — Autorización para eliminar productos del carrito.
 *
 * QUÉ HACE: Toggle por negocio (misma clave que el resto de reglas POS:
 * `cashDrawerSecurityConfig`) que exige contraseña/PIN de dueño o administrador
 * antes de quitar una línea del carrito. Por defecto está DESACTIVADO: el cajero
 * elimina con la confirmación habitual.
 * QUÉ NO DEBE HACER: No elimina líneas ni valida contraseñas (eso vive en
 * `useCartRemovalGuard` / `AdminPasswordConfirmModal`). No persiste directo:
 * usa la capa `cashDrawerSecurityConfig` (transactionalStore + evento).
 */
import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { SwitchRow } from "@/components/ui/Switch";
import {
  loadCashDrawerSecurityConfig,
  normalizeCashDrawerSecurity,
  persistCashDrawerSecurityConfig,
} from "@/features/caja/lib/cashDrawerSecurityConfig";

export function CartRemovalSecuritySettings() {
  const businessId = useAuthStore((s) => s.user?.businessId);
  const [hydrated, setHydrated] = useState(false);
  const [requireAuth, setRequireAuth] = useState(false);

  useEffect(() => {
    setHydrated(false);
    const c = normalizeCashDrawerSecurity(
      loadCashDrawerSecurityConfig(businessId),
    );
    setRequireAuth(c.requireAuthorizationToRemoveCartItem === true);
    setHydrated(true);
  }, [businessId]);

  useEffect(() => {
    const onExternal = () => {
      const c = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      setRequireAuth(c.requireAuthorizationToRemoveCartItem === true);
    };
    window.addEventListener("altenot:cash-drawer-security", onExternal);
    return () =>
      window.removeEventListener("altenot:cash-drawer-security", onExternal);
  }, [businessId]);

  useEffect(() => {
    if (!hydrated) return;
    const id = window.setTimeout(() => {
      const full = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      const next = normalizeCashDrawerSecurity({
        ...full,
        requireAuthorizationToRemoveCartItem: requireAuth,
      });
      const prev = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      if (JSON.stringify(prev) === JSON.stringify(next)) return;
      persistCashDrawerSecurityConfig(businessId, next);
    }, 420);
    return () => window.clearTimeout(id);
  }, [businessId, hydrated, requireAuth]);

  return (
    <SwitchRow
      size="sm"
      icon={
        <ShieldAlert size={16} className="text-slate-600 dark:text-slate-300" />
      }
      label="Autorización para eliminar productos del carrito"
      description={
        requireAuth
          ? "Para eliminar un producto del carrito, se debe introducir la contraseña/PIN del Dueño o Administrador autorizado."
          : "Cualquier cajero autorizado puede eliminar productos después de la confirmación habitual."
      }
      checked={requireAuth}
      onChange={setRequireAuth}
    />
  );
}
